import {CHAT_KEYS} from "@/api/chatApi";
import {COMPANY_KEYS} from "@/api/companyApi";
import useChatParams from "@/hooks/useChatParams";
import {useAgencyId, useIsAgency} from "@/hooks/useUserHooks";
import {useChatSearchParams} from "@/pages/chat/chatHooks";
import useNotificationStore from "@/store/notification";
import type {TCampaignWithChat, TChat, TPaginated} from "@/store/types";
import {useQueryClient, type InfiniteData} from "@tanstack/react-query";
import type {AxiosResponse} from "axios";
import {produce} from "immer";
import pick from "lodash/pick";
import qs from "qs";
import {useCallback} from "react";
import {useNavigate} from "react-router";

const useOpenChat = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [{search}] = useChatSearchParams();
  const isAgency = useIsAgency();
  const agencyId = useAgencyId();
  const {companyId} = useChatParams();
  const onChatOpened = useNotificationStore(state => state.onChatOpened);
  const onOpenChat = useCallback(
    (unreadCount: number, messageId: number, campaignId?: number) => {
      onChatOpened(!!unreadCount);
      queryClient.setQueryData<InfiniteData<AxiosResponse<TPaginated<TChat>>>>(
        CHAT_KEYS.qChatList({companyId}),
        prevState => {
          if (!prevState) return prevState;
          return produce(prevState, draft => {
            for (const page of draft.pages) {
              for (const message of page.data.results) {
                if (message.id === messageId) {
                  message.unread_by_company = 0;
                  break;
                }
              }
            }
          });
        },
      );

      if (!campaignId) {
        return;
      }

      queryClient.setQueryData<InfiniteData<AxiosResponse<TPaginated<TChat>>>>(
        CHAT_KEYS.qChatsOfCampaign({campaignId, search}),
        prevState => {
          if (!prevState) return prevState;
          return produce(prevState, draft => {
            for (const page of draft.pages) {
              for (const message of page.data.results) {
                if (message.id === messageId) {
                  message.unread_by_company = 0;
                  break;
                }
              }
            }
          });
        },
      );

      if (!companyId) {
        return;
      }

      queryClient.setQueryData<InfiniteData<AxiosResponse<TPaginated<TCampaignWithChat>>>>(
        COMPANY_KEYS.qCompanyChatCampaigns(companyId),
        prevState => {
          if (!prevState) return prevState;
          return produce(prevState, draft => {
            for (const page of draft.pages) {
              for (const campaign of page.data.results) {
                if (campaign.id === campaignId) {
                  if (!campaign.unread_count) break;
                  campaign.unread_count -= unreadCount;
                  break;
                }
              }
            }
          });
        },
      );
    },
    [onChatOpened, queryClient, search, companyId],
  );

  return useCallback(
    (params: {
      companyId?: number;
      influencerUserId?: number;
      campaignId?: number;
      messageId?: number;
      unreadCount?: number;
      isBulk?: boolean;
      search?: string;
    }) => {
      const parsedParams = qs.stringify(pick(params, ["isBulk", "search"]));
      if (params.unreadCount && !!params.messageId) {
        onOpenChat(params.unreadCount, params.messageId, params.campaignId);
      }

      let pathname = "";

      if (isAgency) {
        pathname =
          `/agency/${agencyId}/chat/${params.companyId}/${params.campaignId}/` +
          (typeof params.influencerUserId !== "undefined" ? `${params.influencerUserId}/` : "");
      } else {
        pathname =
          `/company/${companyId || params.companyId}/chat/${params.campaignId}/` +
          (typeof params.influencerUserId !== "undefined" ? `${params.influencerUserId}/` : "");
      }

      navigate({
        pathname,
        search: parsedParams,
      });
    },
    [navigate],
  );
};

export {useOpenChat};
